import React, { useState, useEffect, useRef } from 'react';
import Card from './Card';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface CollapsibleSectionProps {
  id: string;
  title: string;
  defaultExpanded?: boolean;
  forceExpanded?: boolean;
  children: React.ReactNode;
  className?: string;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  id,
  title,
  defaultExpanded = true,
  forceExpanded = false,
  children,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (forceExpanded) {
      setIsExpanded(true);
    }
  }, [forceExpanded]);

  const expanded = forceExpanded || isExpanded;

  const handleToggle = () => {
    if (!forceExpanded) {
      setIsExpanded((prev) => !prev);
    }
  };

  return (
    <div id={id}>
    <Card hover className={cn('shadow-sm hover:shadow-md', className)}>
      <div
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <h5 className="text-[16px] font-semibold text-[var(--foreground)]">
          {title}
        </h5>
        <ChevronDown
          className={cn(
            'w-5 h-5 text-[var(--muted-foreground)] transition-transform duration-200',
            expanded ? 'rotate-0' : '-rotate-90'
          )}
        />
      </div>
      <div
        ref={contentRef}
        className="overflow-hidden transition-all duration-200"
        style={{
          maxHeight: expanded ? '5000px' : '0px',
          opacity: expanded ? 1 : 0,
        }}
      >
        <div className="pt-4">{children}</div>
      </div>
    </Card>
    </div>
  );
};

export default CollapsibleSection;
